import { partsQuery } from "@common/api/parts"
import { LoadingState } from "@common/components/shared/loading-state"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { DetailPane } from "@/components/layout/detail-panel"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"
import { useDefaultCurrency } from "@/routes/_layout/quoting_/quotes/-lib/default-currency"
import { PurchaseOrderForm } from "./-components/form/purchase-order-form"
import { MAX_ORDER_ITEMS, newOrderSearchSchema } from "./-lib/order"

function selectedParts(partIds: string[]) {
  return partsQuery({ part_id: partIds, limit: MAX_ORDER_ITEMS })
}

export const Route = createFileRoute("/_layout/inventory_/purchase-orders/new")(
  {
    staticData: { breadcrumb: "New" },
    validateSearch: (search) => newOrderSearchSchema.parse(search),
    beforeLoad: async ({ context: { queryClient }, search }) => {
      await requirePermission(queryClient, "purchase_order:create", "/")
      if (search.parts.length === 0) {
        throw redirect({ to: "/inventory" })
      }
    },
    loaderDeps: ({ search }) => ({ parts: search.parts }),
    loader: ({ context: { queryClient }, deps }) =>
      queryClient.ensureQueryData(selectedParts(deps.parts)),
    component: NewPurchaseOrderPane,
  },
)

function NewPurchaseOrderPane(): React.ReactElement {
  const { parts } = Route.useSearch()
  const { data } = useSuspenseQuery(selectedParts(parts))
  const currency = useDefaultCurrency()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const close = () => navigate({ to: "/inventory" })

  return (
    <DetailPane
      title="New purchase order"
      heading={<span className="truncate">New purchase order</span>}
      onClose={close}
    >
      {currency ? (
        <PurchaseOrderForm
          parts={data.data}
          currency={currency}
          onCreate={(created) => {
            showToast(
              `Created ${created.number}`,
              created.vendor?.name ?? "",
              "success",
            )
            navigate({
              to: "/inventory/purchase-orders/$purchaseOrderId",
              params: { purchaseOrderId: created.id },
            })
          }}
          onCancel={close}
        />
      ) : (
        <LoadingState />
      )}
    </DetailPane>
  )
}
